import React, { FC, useState } from 'react';
import { useLocation, useMatch, useParams } from 'react-router-dom';

import { SearchSortDirections, SearchSortFields } from 'api';
import * as paths from 'paths';
import { Layouts } from 'components';
import { useFollowed } from 'hooks';

import {
  FilterSelect,
  GroupListFollow,
  GroupListModerator,
  GroupListOwner,
  LatestGroups,
  QueryList,
} from '../components/GroupsIndex';

type Params = {
  department_id: string;
  location_id: string;
};

const RESULTS_FROM = 0;
const SIZE = 10;

const GroupsIndex: FC = () => {
  const params = useParams<Params>();
  const { pathname } = useLocation();
  const { groups, followedOnly, moderated, owned } = useFollowed();
  const [followingGroups] = useState(groups);

  const isIndex = pathname === paths.groups;
  const isModerated = pathname === paths.groupsModerating;
  const isOwned = pathname === paths.groupsIOwn;
  const isFollowing = pathname === paths.groupsFollowing;
  const isAll = pathname === paths.groupsAll;
  const isDepartmentFilter = !!useMatch(paths.groupsDepartment);
  const isLocationFilter = !!useMatch(paths.groupsLocation);

  const filterVariables = {
    results_from: RESULTS_FROM,
    size: SIZE,
  };

  if (isIndex) {
    const variables = {
      ...filterVariables,
      auto_exclude: true,
      exclude_group_ids: followingGroups.map(group => group.id),
    };
    return (
      <Layouts.App2Column
        mainWidth="wide"
        sidebar={<FilterSelect />}
        main={
          <Layouts.AppMain2Column
            desktopSplit="8/4"
            left={<QueryList emptyStateName="groupsRecommended" variables={variables} />}
            right={<LatestGroups />}
          />
        }
      />
    );
  }

  let main = null;

  if (isModerated) {
    main = <GroupListModerator emptyStateName="groupsModerated" groups={moderated} />;
  }

  if (isOwned) {
    main = <GroupListOwner emptyStateName="groupsOwned" groups={owned} />;
  }

  if (isFollowing) {
    main = <GroupListFollow emptyStateName="groupsFollowed" groups={followedOnly} />;
  }

  if (isAll) {
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      sort: SearchSortFields.Title,
      sort_direction: SearchSortDirections.Asc,
    };
    main = <QueryList emptyStateName="search" variables={variables} />;
  }

  if (isDepartmentFilter) {
    const { department_id } = params;
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      required_department_id: department_id,
    };

    main = <QueryList emptyStateName="groupsDepartments" variables={variables} />;
  }

  if (isLocationFilter) {
    const { location_id } = params;
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      required_location_id: location_id,
    };

    main = <QueryList emptyStateName="groupsLocations" variables={variables} />;
  }

  return <Layouts.App2Column mainWidth="wide" sidebar={<FilterSelect />} main={main} />;
};

export default GroupsIndex;
